<template>
  <div class="video-section">
    <h1 class="video-section__title">{{ $t('viewFaqs.video_tutorials') }}</h1>
    <div class="video-section__grid">
      <BackstageCard
        v-for="faq in displayedFaqs"
        :key="faq.externalId"
        height="600px"
        :thumbnail-url="faq.video?.thumbnail"
        :video-url="faq.video?.url"
        :title="getTitle(faq)"
        :description="getDescription(faq)"
        :date="formatDate(faq)"
        :duration="faq.video?.duration" />
    </div>
    <div v-if="videoFaqs.length > defaultVisibleCount" class="video-section__see-more">
      <BackstageButton
        :variant="ButtonVariant.PRIMARY"
        :icon-right="Icons.CHEVRON_DOWN"
        id="video-see-more-button"
        data-cy="video-see-more-button"
        @click="toggleShowAll"
        :class="['see-more-button', { 'see-more-button--expanded': isExpanded }]">
        {{ isExpanded ? $t('viewFaqs.expand_button.see_less') : $t('viewFaqs.expand_button.see_more') }}
      </BackstageButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import BackstageButton from '@/components/common/BackstageButton.vue';
import BackstageCard from '@/components/common/BackstageCard.vue';
import { useAnalytics } from '@/composables/analytics';
import { useFormat } from '@/composables/format';
import { useLocale } from '@/composables/locale';
import { globalConstants } from '@/constants/global.constants';
import { ButtonVariant } from '@/models/button-variant.enum';
import { GaCustomEvents } from '@/models/ga-custom-events.enum';
import { Icons } from '@/models/icons.enum';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import type { FaqModel } from '@onrunning/backstage-models';
import { computed, ref } from 'vue';

const { userLanguage } = useLocale();
const { addCustomGaEvent } = useAnalytics();
const { getTranslationFromTranslateField } = useFormat();

const props = defineProps<{
  videoFaqs: FaqModel[];
}>();

const defaultVisibleCount = 3;
const isExpanded = ref(false);

const displayedFaqs = computed(() => {
  if (isExpanded.value) {
    return props.videoFaqs;
  }

  return props.videoFaqs.slice(0, defaultVisibleCount);
});

const toggleShowAll = () => {
  addCustomGaEvent({
    event: isExpanded.value ? GaCustomEvents.VideoTutorialSeeLess : GaCustomEvents.VideoTutorialSeeMore,
    total_videos: props.videoFaqs.length,
  });
  isExpanded.value = !isExpanded.value;
};

const getTitle = (faq: FaqModel): string => {
  return getTranslationFromTranslateField(faq.title);
};

const getDescription = (faq: FaqModel): string => {
  const description = faq.description[userLanguage.value] ?? faq.description[globalConstants.fallBackLocale];

  return documentToPlainTextString(description);
};

const formatDate = (faq: FaqModel): string | undefined => {
  const date = faq.video?.createdAt;

  if (!date) {
    return undefined;
  }

  return new Date(date).toLocaleDateString(userLanguage.value, { year: 'numeric', month: 'long', day: 'numeric' });
};
</script>

<style lang="scss" scoped>
.video-section {
  padding: 2rem 0;

  &__title {
    font-size: 2rem;
    font-weight: 600;
    margin-bottom: 2rem;
    text-align: left;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem;

    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
    }
  }

  &__see-more {
    display: flex;
    justify-content: center;
    margin-top: 2rem;
  }
}

.see-more-button {
  :deep(.icon.right) {
    transition: transform 0.2s ease;
  }

  :deep(.chevron-down) {
    background-color: var(--secondary-text-color);
  }

  &.see-more-button--expanded :deep(.icon.right) {
    transform: rotate(180deg);
  }
}
</style>
